import React, { useRef, useState } from "react";
import { FieldProps } from "@rjsf/utils";
import { Box, Button, CircularProgress, MenuItem, TextField } from "@material-ui/core";
import { Translate } from "@material-ui/icons";
import { translateByLocale } from "shared/localizedText";

type Translation = { locale?: string } & Record<string, unknown>;

const TEXT_KEYS = ["title", "variation_title", "instructions_text1", "about", "tip_title", "tip_description"];

const textOf = (translation: Translation | undefined, key: string) =>
  typeof translation?.[key] === "string" ? (translation[key] as string).trim() : "";

const TranslationsField: React.VFC<FieldProps> = props => {
  const { formData, onChange, registry, disabled, readonly } = props;
  const ArrayField = registry.fields.ArrayField;
  const translations: Translation[] = Array.isArray(formData) ? formData : [];
  const locales = translations.map(t => t.locale).filter((l): l is string => !!l);
  const [chosenSource, setChosenSource] = useState("");
  const [translating, setTranslating] = useState(false);
  const latest = useRef(translations);
  latest.current = translations;

  const source = locales.includes(chosenSource)
    ? chosenSource
    : locales.includes("sv-se")
      ? "sv-se"
      : (locales[0] ?? "");
  const sourceTranslation = translations.find(t => t.locale === source);

  const jobs = translations.flatMap(target =>
    target.locale && target.locale !== source
      ? TEXT_KEYS.filter(key => textOf(sourceTranslation, key) && !textOf(target, key)).map(key => ({
          locale: target.locale as string,
          key,
          text: textOf(sourceTranslation, key),
        }))
      : []
  );

  const translateEmpty = async () => {
    setTranslating(true);
    const results: { locale: string; key: string; text: string }[] = [];
    await translateByLocale(jobs, source, { onTranslated: (job, text) => results.push({ ...job, text }) });
    onChange(
      latest.current.map(target =>
        results.reduce<Translation>(
          (acc, result) =>
            result.locale === target.locale && !textOf(acc, result.key) ? { ...acc, [result.key]: result.text } : acc,
          target
        )
      )
    );
    setTranslating(false);
  };

  return (
    <Box>
      {locales.length > 1 && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Translate from"
            value={source}
            onChange={e => setChosenSource(String(e.target.value))}
            disabled={disabled || readonly || translating}
          >
            {locales.map(locale => (
              <MenuItem key={locale} value={locale}>
                {locale}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            startIcon={translating ? <CircularProgress size={14} /> : <Translate fontSize="small" />}
            onClick={translateEmpty}
            disabled={disabled || readonly || translating || jobs.length === 0}
          >
            Fill empty fields ({jobs.length})
          </Button>
        </Box>
      )}
      <ArrayField {...props} />
    </Box>
  );
};

export default TranslationsField;
